import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Plus, Instagram, Mail, Phone } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { API } from "@/lib/cms";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/about", label: "About" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const Home = () => {
  const { hero, logo, about, categories, reviews, faqs, contact } = useContent();
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", session_type: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const descParts = (hero.description || "").split(/(?<=\.)\s+/).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email, and a message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Your inquiry has been received. I'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", session_type: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-black/20 py-3 text-[#1a1a1a] placeholder:text-black/40 focus:outline-none focus:border-[#1a1a1a] transition-colors duration-500";

  return (
    <div data-testid="home-page" className="bg-[#F4EFE6] text-[#1a1a1a]">
      {/* ============ PANORAMIC HERO ============ */}
      <section data-testid="hero-section" className="relative w-full h-[60vh] md:h-auto overflow-hidden">
        <img src={hero.image} alt="Ghajoyan Photo — cinematic wedding" className="block w-full h-full object-cover object-[62%_28%] md:h-auto md:object-cover" />
        <div className="absolute inset-0 bg-black/30" />

        <div className="absolute inset-0 z-10 flex flex-col">
          <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 pt-6 flex items-center justify-between">
            <Link to="/" data-testid="logo-link" className="block">
              <img src={logo} alt="Ghajoyan Photo" className="h-11 md:h-14 w-auto" />
            </Link>
            <nav className="hidden md:flex items-center gap-8 lg:gap-10">
              {NAV.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`} className="text-[0.65rem] uppercase tracking-[0.25em] text-white/85 hover:text-gold transition-colors duration-500">
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex-1 w-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="max-w-xl">
              <p className="font-serif text-2xl md:text-3xl text-white tracking-tight">{hero.headline}</p>
              <p className="text-[0.65rem] uppercase tracking-[0.35em] text-gold mt-2">{hero.subheadline}</p>
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-white leading-tight tracking-tight mt-4">
                {descParts[0]}{descParts[1] && <><br /><span className="italic text-beige">{descParts[1]}</span></>}
              </h1>
              <Link to="/portfolio" data-testid="hero-view-portfolio" className="group inline-flex items-center gap-2 mt-7 border border-white/50 px-7 py-3 text-[0.65rem] uppercase tracking-[0.25em] text-white hover:bg-white hover:text-[#1a1a1a] transition-colors duration-500">
                {hero.buttonText} <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform duration-500" />
              </Link>
            </motion.div>
          </div>

          <div className="md:hidden w-full px-6 pb-3">
            <nav className="flex flex-wrap gap-x-5 gap-y-1">
              {NAV.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase()}`} className="text-[0.6rem] uppercase tracking-[0.2em] text-white/75">
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </section>

      {/* ============ FEATURED PORTFOLIO + ABOUT ============ */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-24 grid md:grid-cols-12 gap-14 md:gap-20">
        <motion.div {...fade} className="md:col-span-7" data-testid="featured-portfolio">
          <div className="flex items-baseline justify-between border-b border-black/15 pb-4 mb-6">
            <h2 className="font-serif text-3xl md:text-4xl font-light">Featured Portfolio</h2>
            <Link to="/portfolio" data-testid="featured-all-link" className="text-[0.6rem] uppercase tracking-[0.25em] text-black/60 hover:text-[#1a1a1a] transition-colors">All →</Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8">
            {categories.map((c, i) => (
              <Link key={c.slug} to={`/portfolio/${c.slug}`} data-testid={`featured-${c.slug}`} className="group flex items-center gap-4 py-4 border-b border-black/10">
                <div className="w-14 h-14 overflow-hidden shrink-0">
                  <img src={c.cover} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="flex-1 flex items-baseline justify-between">
                  <span className="font-serif text-xl md:text-2xl font-light group-hover:text-gold transition-colors duration-500">{c.title}</span>
                  <span className="text-[0.6rem] text-black/40 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                </div>
              </Link>
            ))}
          </div>
        </motion.div>

        <motion.div {...fade} className="md:col-span-5" data-testid="about-block">
          <div className="border-b border-black/15 pb-4 mb-6">
            <h2 className="font-serif text-3xl md:text-4xl font-light">{about.title}</h2>
          </div>
          <div className="overflow-hidden">
            <img src={about.image} alt="Photographer portrait" className="w-full aspect-[4/5] object-cover" />
          </div>
          {(about.paragraphs || []).map((p, i) => (
            <p key={i} className={`text-black/70 leading-relaxed ${i === 0 ? "mt-6" : "mt-4"}`}>{p}</p>
          ))}
          <Link to="/about" data-testid="about-read-more" className="inline-flex items-center gap-2 mt-6 text-[0.65rem] uppercase tracking-[0.25em] border-b border-[#1a1a1a] pb-1 hover:text-gold hover:border-gold transition-colors">
            Read the Full Story
          </Link>
        </motion.div>
      </section>

      {/* ============ CLIENT REVIEWS ============ */}
      <section className="bg-[#EFE9DD] border-y border-black/10 py-20 md:py-24" data-testid="reviews-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <motion.div {...fade} className="text-center mb-14">
            <p className="text-[0.6rem] uppercase tracking-[0.35em] text-gold mb-3">Kind Words</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light tracking-tight">Client Reviews</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {reviews.slice(0, 3).map((r, i) => (
              <motion.div key={i} {...fade} transition={{ ...fade.transition, delay: i * 0.1 }} data-testid={`review-card-${i}`} className="border border-black/15 bg-[#F4EFE6] p-8 flex flex-col justify-between">
                <div>
                  <span className="font-serif text-4xl text-gold/50 leading-none block mb-3">"</span>
                  <blockquote className="font-serif text-xl md:text-2xl font-light leading-snug">{r.quote}</blockquote>
                </div>
                <div className="mt-8 pt-5 border-t border-black/10">
                  <p className="font-serif text-lg">{r.name}</p>
                  <p className="text-[0.6rem] uppercase tracking-[0.3em] text-black/50 mt-1">{r.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-24" data-testid="faq-section">
        <motion.div {...fade} className="text-center mb-12">
          <p className="text-[0.6rem] uppercase tracking-[0.35em] text-gold mb-3">Questions</p>
          <h2 className="font-serif text-3xl md:text-5xl font-light tracking-tight">FAQ</h2>
        </motion.div>
        <div>
          {faqs.map((f, i) => (
            <motion.div key={i} {...fade} transition={{ ...fade.transition, delay: i * 0.05 }} className="border-t border-black/15 last:border-b" data-testid={`faq-item-${i}`}>
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`faq-toggle-${i}`} className="w-full flex items-center justify-between gap-6 py-6 text-left group">
                <span className="font-serif text-xl md:text-2xl font-light group-hover:text-gold transition-colors duration-500">{f.q}</span>
                <motion.span animate={{ rotate: openFaq === i ? 45 : 0 }} transition={{ duration: 0.4 }} className="text-gold shrink-0"><Plus size={20} /></motion.span>
              </button>
              <AnimatePresence initial={false}>
                {openFaq === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
                    <p className="pb-7 pr-8 text-black/65 leading-relaxed max-w-2xl">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ CONTACT ============ */}
      <section className="bg-[#EFE9DD] border-t border-black/10 py-20 md:py-24" data-testid="contact-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-14">
          <motion.div {...fade} className="md:col-span-5">
            <p className="text-[0.6rem] uppercase tracking-[0.35em] text-gold mb-3">Inquire</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light tracking-tight leading-none">Tell me your <span className="italic">story.</span></h2>
            <p className="mt-6 text-black/65 leading-relaxed max-w-md">
              Every collaboration begins with a conversation. Share a little about your vision, and I'll personally respond within two business days.
            </p>
            <div className="mt-8 space-y-2">
              <p className="font-serif text-xl">{contact.location}</p>
              <a href={`tel:${contact.phoneLink}`} data-testid="contact-phone" className="flex items-center gap-2 text-black/70 hover:text-gold transition-colors"><Phone size={15} /> {contact.phone}</a>
              <a href={`https://instagram.com/${contact.instagram}`} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="flex items-center gap-2 text-black/70 hover:text-gold transition-colors"><Instagram size={15} /> @{contact.instagram}</a>
              <a href={`mailto:${contact.email}`} data-testid="contact-email" className="flex items-center gap-2 text-black/70 hover:text-gold transition-colors"><Mail size={15} /> {contact.email}</a>
            </div>
          </motion.div>

          <motion.div {...fade} className="md:col-span-6 md:col-start-7">
            <form onSubmit={submit} data-testid="inquiry-form" className="space-y-7">
              <div className="grid sm:grid-cols-2 gap-7">
                <input data-testid="input-name" className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <input data-testid="input-email" type="email" className={inputCls} placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)} />
                <input data-testid="input-phone" className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                <select data-testid="select-session-type" className={inputCls + " cursor-pointer [&>option]:bg-[#EFE9DD]"} value={form.session_type} onChange={(e) => set("session_type", e.target.value)}>
                  <option value="">Session type</option>
                  {categories.map((c) => <option key={c.slug} value={c.title}>{c.title}</option>)}
                </select>
              </div>
              <textarea data-testid="input-message" rows={3} className={inputCls + " resize-none"} placeholder="Tell me about your vision..." value={form.message} onChange={(e) => set("message", e.target.value)} />
              <button type="submit" data-testid="send-inquiry-btn" disabled={sending} className="w-full border border-[#1a1a1a] py-4 text-[0.65rem] uppercase tracking-[0.3em] hover:bg-[#1a1a1a] hover:text-[#F4EFE6] transition-colors duration-500 disabled:opacity-50">
                {sending ? "Sending..." : "Send Inquiry"}
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
